import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { QuoteForm } from "@/components/layout/QuoteForm";
import { SubmissionSuccess } from "@/components/forms/SubmissionSuccess";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import {
  buildSubmissionContext,
  SUBMISSION_SOURCES,
  type SubmissionSource,
} from "@/lib/submission-context";

/**
 * The quote form in a drawer over the current page.
 *
 * The product page used to navigate to /request-quote carrying `product` and
 * `category` in the search string — both of which it already knew. The
 * navigation therefore added no information and cost the buyer the specs, MOQ
 * and image at exactly the moment they were describing what they needed.
 *
 * A full inline form would have been worse: QuoteForm is 17 fields, so
 * embedding it in every product page pushes the ranking content down and
 * leaves two form implementations to keep in sync. Wrapping the existing
 * component keeps one implementation and one validation schema.
 *
 * /request-quote stays a real route: direct links, campaigns and multi-product
 * RFQs from the cart all still need a URL of their own.
 */
export function QuoteDrawer({
  open,
  onOpenChange,
  productSlug,
  productTitle,
  initialPromoCode,
  source = SUBMISSION_SOURCES.productDrawer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productSlug?: string;
  productTitle?: string;
  initialPromoCode?: string;
  source?: SubmissionSource;
}) {
  const { t, lang } = useI18n();
  const { submitQuote } = useCart();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ reference: string | null } | null>(null);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    // Reset to the form when the drawer is reopened, so a second enquiry does
    // not land on the previous success panel.
    if (!next) setDone(null);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
        aria-describedby={undefined}
      >
        <div className="p-6 sm:p-8">
          <SheetHeader className="mb-6 text-start">
            <SheetTitle className="font-display text-2xl font-bold text-navy">
              {t("quote.title")}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              {productTitle
                ? `${t("quote.drawer.forProduct")} ${productTitle}`
                : t("quote.page.descGeneral")}
            </SheetDescription>
          </SheetHeader>

          {done ? (
            <SubmissionSuccess
              title={t("quote.success.title")}
              body={t("quote.success.body")}
              reference={done.reference}
              className="rounded-2xl border border-border/80 bg-surface-alt/60 px-6 py-8"
            >
              <Button
                variant="outline"
                className="rounded-xl font-bold"
                onClick={() => handleOpenChange(false)}
              >
                {t("quote.drawer.close")}
              </Button>
            </SubmissionSuccess>
          ) : (
            <>
              <QuoteForm
                busy={busy}
                formSource={source}
                initialProductSlug={productSlug}
                initialPromoCode={initialPromoCode}
                onSubmit={async (data) => {
                  setBusy(true);
                  try {
                    const reference = await submitQuote(
                      data,
                      buildSubmissionContext(source, { locale: lang, pageSection: productSlug }),
                    );
                    setDone({ reference });
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : t("quote.drawer.error"));
                  } finally {
                    setBusy(false);
                  }
                }}
              />

              {/* Escape hatch to the full page, which carries the trust banner,
                  SLA copy and the multi-product cart flow this drawer omits. */}
              <div className="mt-6 border-t border-border/60 pt-4 text-center">
                <Link
                  to="/request-quote"
                  search={productSlug ? { product: productSlug } : {}}
                  onClick={() => handleOpenChange(false)}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:text-navy transition-colors"
                >
                  {t("quote.drawer.fullPage")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
